import { Box, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import Axios from '../../../config/axios';
import { UrlConstant } from "../../../commom/UrlConstant";
import { useNavigate } from "react-router-dom";
import { AccessDeniedUI } from "../SharedComponent/AccessDeniedUI";
import { usePriceFormatter } from "../../../hooks";

interface CryptoInfo {
    logo: string;
    name: string;
    symbol: string;
    formatted_symbol?: string;
    currency_pair?: string;
    cmc_id?: number;
}

interface MyTradesData {
    _id: string;
    type: string;
    side: string;
    fees: number;
    filledAmount: number;
    filledQuantity: number;
    amount: number;
    avgPrice: number;
    currencyPair: string;
    status: string;
    createdAt: string;
    completedAt: string;
    assetType: string;
    crypto: CryptoInfo;
}

const FIXED_SCROLL_HEIGHT = "500px";

const BADGE_CONFIG = {
    crypto: { label: "Crypto", bg: "#F2674A", color: "#000000", border: "#F2674A" },
    stock: { label: "Stock", bg: "#DFDD4E", color: "#000000", border: "#DFDD4E" },
    etf: { label: "ETF", bg: "#4EDF6D", color: "#000000", border: "#4EDF6D" },
};

const customStyles = {
    headCells: {
        style: {
            fontSize: "14px",
            width: '100% !important',
            justifyContent: "left",
            whiteSpace: "wrap",
            overflow: "hidden",
            color: "white",
            borderBottom: "1px solid #ffffff33 !important",
            fontWeight: "600",
            backgroundColor: "transparent",
            paddingBottom: "12px",
            paddingTop: "16px",
        },
    },
    cells: {
        style: {
            justifyContent: "left",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            color: "white",
            fontSize: '16px',
            border: "none",
            cursor: 'pointer',
            padding: "12px 8px",
            transition: "all 0.1s ease-in-out",
            backgroundColor: "transparent",
        },
    },
    rows: {
        style: {
            borderColor: "transparent !important",
            backgroundColor: 'transparent',
            width: '96% !important',
            borderRadius: "8px",
            margin: '2px 8px',
            padding: "4px",
            transition: "all 0.3s ease",
            cursor: 'pointer',
            outline: "none !important",
            '&:hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
                outline: "none !important",
            },
            '&:nth-child(even)': {
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: "1px solid transparent !important",
                borderColor: "transparent !important",
            },
            '&:nth-child(odd)': {
                backgroundColor: 'transparent',
                border: "1px solid transparent !important",
                borderColor: "transparent !important",
            },
            '&:nth-child(even):hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
            },
            '&:nth-child(odd):hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
            },
        },
    },
    headRow: {
        style: {
            backgroundColor: 'transparent',
        },
    },
};

interface MyTradesProps {
    userId: string;
}

function MyTrades({ userId }: MyTradesProps) {
    const [loading, setLoading] = useState<boolean>(true);
    const [data, setData] = useState<MyTradesData[]>([]);
    const [page, setPage] = useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [totalRows, setTotalRows] = useState(0);
    const [isAccessDenied, setIsAccessDenied] = useState(false);
    const navigate = useNavigate();
    const tableRef = useRef<HTMLDivElement>(null);
    const formatter = usePriceFormatter();


    const columns = [
        {
            name: 'Asset',
            cell: (row: MyTradesData) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {row.crypto?.logo && (
                        <img
                            src={row.crypto.logo}
                            alt={row.crypto.name}
                            style={{ width: 32, height: 32, borderRadius: '50%' }}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                    )}
                    <div>
                        <div style={{ fontWeight: 600, color: 'white' }}>{row.crypto?.name || row.currencyPair}</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{row.crypto?.symbol}</div>
                    </div>
                </div>
            ),
            grow: 2,
        },
        {
            name: 'Side',
            cell: (row: MyTradesData) => (
                <span style={{
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    background: row.side === 'BUY' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                    color: row.side === 'BUY' ? '#22c55e' : '#ef4444',
                    border: `1px solid ${row.side === 'BUY' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                }}>
                    {row.side}
                </span>
            ),
            grow: 1,
        },
        {
            name: 'Type',
            width: "110px",
            cell: (row: MyTradesData) => {
                const key = row.assetType?.toLowerCase() as keyof typeof BADGE_CONFIG;
                const badge = BADGE_CONFIG[key] ?? BADGE_CONFIG.stock;
                return (
                    <span style={{
                        padding: '4px 12px',
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600,
                        background: badge.bg,
                        color: badge.color,
                        border: `1px solid ${badge.border}`,
                    }}>
                        {badge.label}
                    </span>
                );
            },
            grow: 1,
        },
        {
            name: 'Avg Price',
            cell: (row: MyTradesData) => (
                <span style={{ color: 'white' }}>{formatter.price(row.avgPrice).display ?? '—'}</span>
            ),
            grow: 1,
        },
        {
            name: 'Date',
            cell: (row: MyTradesData) => (
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                    {row.completedAt
                        ? new Date(row.completedAt).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                        })
                        : '—'}
                </span>
            ),
            grow: 1,
        },
    ];

    const fetchTrades = async (pageNum: number = 1, limitNum: number) => {
        try {
            setLoading(true);
            const offset = (pageNum - 1) * limitNum;
            const response = await Axios.get(
                UrlConstant.Profile.userAssetData.replace("{0}", userId) +
                `?type=trade&limit=${limitNum}&offset=${offset}`
            );
            if (response.status) {
                setData(response.data.data);
                setTotalRows(response.data.pagination.total);
            }
        } catch (error: any) {
            if (
                error?.response?.data?.status === "fail" &&
                error?.response?.data?.message?.includes("You are not allowed to view this profile")
            ) {
                setIsAccessDenied(true);
            } else {
                console.error('Failed to fetch trades:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) fetchTrades(page, rowsPerPage);
    }, [userId, page, rowsPerPage]);

    useEffect(() => {
        const addClickHandlers = () => {
            if (tableRef.current && data?.length > 0) {
                const possibleSelectors = [
                    'tbody tr',
                    'tr[data-tid="dataTable-row-0"]',
                    'table tr',
                    '.rdt_TableBody tr',
                    '.rdt_TableBodyRowGroup tr',
                ];

                let rows: NodeList | null = null;
                for (const selector of possibleSelectors) {
                    rows = tableRef.current.querySelectorAll(selector);
                    if (rows.length > 0) break;
                }

                if (!rows || rows.length === 0) {
                    tableRef.current.addEventListener('click', (event) => {
                        const target = event.target as HTMLElement;
                        const isButton = target.closest(
                            'button, a, [role="button"], input, select, textarea, .MuiButton-root, .MuiIconButton-root, svg, path'
                        );
                        if (isButton) return;

                        const potentialRowElements = [
                            target.closest('[class*="rdt_TableRow"]'),
                            target.closest('[id^="row-"]'),
                            target.closest('[class*="rdt_TableBodyRow"]'),
                            target.closest('[role="row"]'),
                            target.closest('[data-tid*="row"]'),
                        ].filter((el): el is Element => el !== null);

                        const filteredElements = potentialRowElements.filter((el): el is Element => {
                            if (!el) return false;
                            const className = el.className?.toString?.() || '';
                            return !className.includes('dark-tbl') && !className.includes('no-border');
                        });

                        const actualRow =
                            filteredElements.find((el): el is Element => {
                                if (!el) return false;
                                const className = el.className?.toString?.() || '';
                                return className.includes('rdt_TableRow');
                            }) || filteredElements[0];

                        if (actualRow) {
                            if (!tableRef.current) return;
                            const allTableRows = Array.from(
                                tableRef.current.querySelectorAll('[class*="rdt_TableRow"]')
                            );
                            const rowIndex = allTableRows.indexOf(actualRow);
                            if (rowIndex >= 0 && rowIndex < data.length && data[rowIndex]) {
                                handleNavigation(data[rowIndex]);
                            }
                        }
                    });
                    return;
                }

                rows.forEach((row, index) => {
                    row.addEventListener('click', (event) => {
                        const target = event.target as HTMLElement;
                        const isButton = target.closest(
                            'button, a, [role="button"], input, select, textarea, .MuiButton-root, .MuiIconButton-root, svg, path'
                        );
                        if (isButton) return;

                        const rowData = data[index];
                        if (rowData) {
                            event.preventDefault();
                            event.stopPropagation();
                            handleNavigation(rowData);
                        }
                    }, false);
                });
            }
        };

        const timer = setTimeout(addClickHandlers, 500);
        return () => clearTimeout(timer);
    }, [data]);

    const handleNavigation = (row: MyTradesData) => {
        navigate('/crypto-detail', { state: { externalID: row.crypto?.cmc_id } });
    };

    const handlePageChange = (newPage: number) => setPage(newPage);
    const handleRowsPerPageChange = (newLimit: number) => setRowsPerPage(newLimit);

    if (isAccessDenied) return <AccessDeniedUI />;

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="calc(100vh - 71px - 143px)">
                <CircularProgress style={{ color: '#FFA500' }} />
            </Box>
        );
    }

    if (!loading && data.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography variant="h6" style={{ color: 'white' }}>No Trades Found</Typography>
            </Box>
        );
    }

    return (
        <Box
            className="custom-table-container"
            sx={{
                border: '1px solid #ffffff33',
                borderRadius: '20px',
                // height: FIXED_SCROLL_HEIGHT,
                // overflow: 'hidden',
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <style>{`
                .rdt_TableWrapper,
                .rdt_Table,
                .rdt_TableHead,
                .rdt_TableHeadRow,
                .rdt_TableBody,
                .rdt_TableRow,
                .rdt_Pagination,
                [class*="sc-"] {
                    background-color: transparent !important;
                }

                div[class*="ProgressWrapper"],
                div[role="progressbar"],
                .rdt_Table > div:first-child {
                    background-color: transparent !important;
                }

                .rdt_TableCol input[type="checkbox"],
                .rdt_TableCell input[type="checkbox"] {
                    appearance: none;
                    -webkit-appearance: none;
                    width: 24px;
                    height: 24px;
                    border-radius: 6px;
                    border: 1.5px solid rgba(255, 255, 255, 0.35);
                    background-color: transparent;
                    cursor: pointer;
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.15s ease;
                }
                .rdt_TableCol input[type="checkbox"]:hover,
                .rdt_TableCell input[type="checkbox"]:hover {
                    border-color: rgba(255, 255, 255, 0.6);
                }
                .rdt_TableCol input[type="checkbox"]:checked,
                .rdt_TableCell input[type="checkbox"]:checked {
                    background-color: #4A7AF2;
                    border-color: #4A7AF2;
                }
                .rdt_TableCol input[type="checkbox"]:checked::after,
                .rdt_TableCell input[type="checkbox"]:checked::after {
                    content: "";
                    position: absolute;
                    width: 9px;
                    height: 5px;
                    border-left: 2px solid #ffffff;
                    border-bottom: 2px solid #ffffff;
                    transform: rotate(-45deg);
                }
                .rdt_TableWrapper, .rdt_Table, .rdt_TableHead, .rdt_TableHeadRow,
                .rdt_TableBody, .rdt_TableRow, .rdt_Pagination {
                    color: white !important;
                }
                .custom-table-container button:disabled {
                    fill: #a3a3a370 !important;
                    color: #a3a3a370 !important;
                }
                .custom-table-container button {
                    fill: #FFFFFF !important;
                    color: #FFFFFF !important;
                }
                .rdt_TableHead {
                overflow:hidden !important;
                }
            `}</style>

            <div
                ref={tableRef}
                style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
                <DataTable
                    columns={columns}
                    data={data}
                    style={{ backgroundColor: 'transparent' }}
                    // fixedHeader={true}
                    // fixedHeaderScrollHeight={`calc(${FIXED_SCROLL_HEIGHT} - 56px)`}
                    fixedHeader={false}
                    onChangePage={handlePageChange}
                    responsive={true}
                    highlightOnHover={true}
                    pointerOnHover={true}
                    dense={false}
                    persistTableHead={false}
                    noHeader={false}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    paginationTotalRows={totalRows}
                    customStyles={customStyles}
                    pagination
                    paginationServer
                    paginationDefaultPage={page}
                    paginationPerPage={rowsPerPage}
                    paginationRowsPerPageOptions={[5, 10, 20, 50]}
                    progressComponent={
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "40px",
                            backgroundColor: "transparent",
                            minHeight: "200px",
                        }}>
                            <CircularProgress size="40px" style={{ color: "#4A7AF2" }} />
                        </div>
                    }
                    noDataComponent={
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            padding: '24px',
                            color: "white",
                            fontSize: "16px",
                            backgroundColor: "transparent",
                        }}>
                            There are no records to display
                        </div>
                    }
                />
            </div>
        </Box>
    );
}

export default MyTrades;